//SalesSearchSettings.ts ==============================================================
import "../Util/Prototypes";

//ESRI javascript API.
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";

export interface iPropType {
   name: string;
   layerName: string;
   where: string;
   image1: string;
   criteriaKeys: string[];
}

export interface iOmitImproveTypeCriteria {
   improveType: string;
   criteriaKeys: string[];
}

export interface iCriteria {
   field: string;
   subjectField: string;
   rangePercent: number;
   weighting: number;
   convertSubjectToAcres?: boolean;
   convertSalesToAcres?: boolean;
   alias: string;
   subjectValue?: number;
   minValue?: number;
   maxValue?: number;
}

export interface iCriteriaDropdown {
   field: string;
   alias: string;
   values: string[];
   value: string;
   defaultValue: string;
}

export interface iSaleDate {
   field: string;
   alias: string;
   weighting: number;
}

export interface iImproveType {
   name: string;
   vacant?: boolean;
   where: string;
}

export interface iSubjectField {
   pin: string;
   proptype: string;
   siteaddress: string;
   yearbuilt: string;
   bedrooms: string;
   bathrooms: string;
   halfbaths: string;
   propusecode_sales: string;
   subdivision: string;
   constructiontype: string;
   garage: string;
   lakefront: string;
   unfinbsmtsqft: string;
   saleprice: string;
   saletype: string;
   sqft: string;
}

export interface iSS {
   propTypes: iPropType[];
   propertyTypes: string[];
   improveTypes: iImproveType[];
   improvementTypes: string[];
   omitImproveTypeCriterias: iOmitImproveTypeCriteria[];
   omitVacantFields: string[];

   StructureSize: iCriteria;
   BasementSqFt: iCriteria;
   LotSize: iCriteria;
   YearBuilt: iCriteria;
   Bedrooms: iCriteria;
   Bathrooms: iCriteria;
   Condition: iCriteria;
   Quality: iCriteria;
   SalePrice: iCriteria;

   HouseStyle: iCriteriaDropdown;
   ExteriorMaterial: iCriteriaDropdown;
   Garage: iCriteriaDropdown;
   Grade: iCriteriaDropdown;
   BizType: iCriteriaDropdown;
   OnLake: iCriteriaDropdown;
   SaleType: iCriteriaDropdown;

   saleDate: iSaleDate;
   saleDate_spans: string[];
   distanceWeighting: number;
   subjectCentroid: __esri.Point;
   salesPointsMapTheme: string;

   autoSearch: boolean;
   neighborhood_layerName: string;
   geomSym: __esri.SimpleFillSymbol;

   subjectField_PropType: string;
   subjectField_ImproveType: string;

   subjectField: iSubjectField;
   accessLevel: number;
   accessLevel_neighborhood: number;
   accessLevel_salesAverage: number;
   accessLevel_report: number;
   searchRadius_list: string[];
   salesResultsTools: string[];
   salesResultsMoreTools: string[];
}

export const ss = {} as iSS; //Weird syntax.
const s: iSS = ss;

// Property type settings. Custom!
// Symbols: https://developers.arcgis.com/net/store/api-reference/html/6a641604-55f8-4173-88ce-9d0c9791db24.htm
// Format: Property_Type_Value, LayerName, Where_SQL, Icon_Image, Case_Sensitive_Criteria_Keys...
s.propTypes = [
   {
      name: "Any", layerName: "All Property Types", where: "", image1: "Shapes/YellowCircleLargeB.png",
      criteriaKeys: ["StructureSize", "LotSize", "YearBuilt", "Bedrooms", "Bathrooms", "SalePrice", "ExteriorMaterial", "Garage", "OnLake", "SaleType"]
   },
   {
      name: "Residential", layerName: "Residential", where: "PropType = 'Residential'", image1: "PeoplePlaces/Housing.png",
      criteriaKeys: ["StructureSize", "BasementSqFt", "LotSize", "YearBuilt", "Bedrooms", "Bathrooms", "SalePrice", "ExteriorMaterial", "Garage", "OnLake", "SaleType"]
   },
   {
      name: "Multi-Family", layerName: "Multi-Family", where: "PropType = 'Multi-Family'", image1: "PeoplePlaces/Stadium.png",
      criteriaKeys: ["StructureSize", "LotSize", "YearBuilt", "Bedrooms", "Bathrooms", "SalePrice", "SaleType"]
   },
   {
      name: "Mobile Home", layerName: "Mobile Home", where: "PropType = 'Mobile Home'", image1: "PeoplePlaces/esriBusinessMarker_52_Yellow.png",
      criteriaKeys: ["StructureSize", "LotSize", "YearBuilt", "Bedrooms", "Bathrooms", "SalePrice", "OnLake", "SaleType"]
   },
   {
      name: "Commercial", layerName: "Commercial", where: "PropType = 'Commercial'", image1: "PeoplePlaces/City.png",
      criteriaKeys: ["StructureSize", "LotSize", "YearBuilt", "SalePrice", "SaleType"]
   },
   {
      name: "Farm/Timber", layerName: "Farm / Timber", where: "PropType = 'Farm/Timber'", image1: "PeoplePlaces/Forest.png",
      criteriaKeys: ["StructureSize", "LotSize", "SalePrice", "SaleType"]
   },
   {
      name: "Other", layerName: "Other", where: "PropType = 'Other'", image1: "PeoplePlaces/InformationQuestion.png",
      criteriaKeys: ["StructureSize", "LotSize", "YearBuilt", "Bedrooms", "Bathrooms", "SalePrice", "SaleType"]
   },
];

s.propertyTypes = [];
for (const propType of s.propTypes) {
   s.propertyTypes.push(propType.name);
}

//Improvement types ("name" value is case sensitive). Building with Land, Land Only, Building Only.
s.improveTypes = [{ name: "Any", where: "" },
   { name: "Improved", vacant: false, where: "SaleClass = 'Improved'" },
   { name: "Unimproved", vacant: true, where: "SaleClass = 'Unimproved'" }];

s.improvementTypes = [];
for (const improveType of s.improveTypes) {
   s.improvementTypes.push(improveType.name);
}

//Omit search criteria based on sale type. Format: Sale_Type_Value, Criteria_Keys_To_Omit...
s.omitImproveTypeCriterias = [{
   improveType: "Unimproved", criteriaKeys: ["StructureSize", "BasementSqFt", "YearBuilt", "Bedrooms", "Bathrooms", "ExteriorMaterial", "Garage"]
}];

//Omit display fields for vacant properties.
s.omitVacantFields = ["SqFt", "YearBuilt", "Bedrooms", "Bathrooms", "HalfBaths", "ConstructionType", "Garage", "FinBsmtSqft", "UnfinBsmtSqft"];

// Case sensitive range criteria Keys and sales field names.  Subject field names are not case sensitive.
// Percent offset is for pre-popluating ranges from subject values.
// Set percent to -1 to not initialize range search.
// Weighting values are 1 to 5
// Format: Criteria_Key = Sales_Field, subject_Property_Field, alias_prompt, Percent offset, Weighting
s.StructureSize = { field: "SqFt", subjectField: "SqFt", alias: "Structure SqFt", rangePercent: 50, weighting: 5 };
s.BasementSqFt = { field: "FinBsmtSqft", subjectField: "FinBsmtSqft", alias: "Basement SqFt", rangePercent: -1, weighting: 2 };
s.LotSize = { field: "Area_SqFt", subjectField: "Area", alias: "Lot Size (acres)", convertSubjectToAcres: true, convertSalesToAcres: true, rangePercent: 450, weighting: 2 };
s.YearBuilt = { field: "YearBuilt", subjectField: "YearBuilt", alias: "Year Built", rangePercent: 2, weighting: 2 };
s.Bedrooms = { field: "Bedrooms", subjectField: "Bedrooms", alias: "Bedrooms", rangePercent: 100, weighting: 1 };
s.Bathrooms = { field: "Bathrooms", subjectField: "Bathrooms", alias: "Bathrooms", rangePercent: -1, weighting: 1 };
s.Condition = { field: "", subjectField: "", alias: "Condition", rangePercent: 0, weighting: 0 };
s.Quality = { field: "", subjectField: "", alias: "Quality", rangePercent: 0, weighting: 0 };
s.SalePrice = { field: "SalePrice", subjectField: "MarketValue_Total", alias: "Market & Sale Price", rangePercent: 70, weighting: 0 };

//Drop-down list criterial.
s.HouseStyle = { field: "", alias: "House Style", values: ["Any", "Ranch", "Two Story"], value: "Any", defaultValue: "Any" };
s.ExteriorMaterial = { field: "ConstructionType", alias: "Exterior Material", values: ["Any", "Block", "Brick", "Frame", "HardBoar", "Masonite", "Metal", "Siding", "Steel", "Stone", "Stucco", "Vinyl", "Wood"], value: "Any", defaultValue: "Any" };
s.Garage = { field: "Garage", alias: "Garage", values: ["Any", "Yes", "No", "Carport"], value: "Any", defaultValue: "Any" };
s.Grade = { field: "", alias: "Quality Grade", values: ["Any", "A", "B", "C"], value: "Any", defaultValue: "Any" };
s.BizType = { field: "", alias: "Biz Type", values: ["Any",], value: "Any", defaultValue: "Any" };
s.OnLake = { field: "LakeFront", alias: "On Lake Gwd", values: ["Any", "Y", "N"], value: "Any", defaultValue: "Any" };
s.SaleType = { field: "SaleType", alias: "Sale Type", values: ["Any", "Valid", "Invalid", "Combination"], value: "Valid", defaultValue: "Valid" };

//Sale date. Case sensitive field name.
s.saleDate = { field: "SaleDate", alias: "Sale Date", weighting: 1 };
s.saleDate_spans = ["Custom", "1 Month", "3 Months", "6 Months", "9 Months", "1 Year", "2 Years", "3 Years", "4 years", "5 Years", "10 Years"];

//Distance from subject property weighting.
s.distanceWeighting = 2;
s.subjectCentroid = null;

//Sales points map theme must have the first property type layer name in it.
s.salesPointsMapTheme = "Sales Points";

//Neighborhood polygon layer.
s.neighborhood_layerName = "Neighborhoods";
s.geomSym = new SimpleFillSymbol({ color: [0, 0, 0, 0], style: "solid", outline: { color: [0, 180, 255, 1], width: 2 } });

//Property type and improvement type parcel fields to build dropdowns.
s.subjectField_PropType = "PropType";
s.subjectField_ImproveType = "TaxValue_Improvement";

//Subject parcel fields for comp report. Format: s.subjectField.lowercase_sales_field_name_here = "parcel_field_name_here";
s.subjectField = {} as iSubjectField; //Weird syntax.

s.subjectField.pin = "pin";
s.subjectField.proptype = "PropType";
s.subjectField.siteaddress = "SiteAddress";
s.subjectField.yearbuilt = "YearBuilt";
s.subjectField.bedrooms = "Bedrooms";
s.subjectField.bathrooms = "Bathrooms";
s.subjectField.halfbaths = "HalfBaths";
s.subjectField.propusecode_sales = "PropUseCode";
s.subjectField.subdivision = "Subdivision";
s.subjectField.constructiontype = "ConstructionType";
s.subjectField.garage = "Garage";
s.subjectField.lakefront = "LakeFront";
s.subjectField.unfinbsmtsqft = "UnFinBsmtSqFt";
s.subjectField.saleprice = "MarketValue_Total";
s.subjectField.saletype = "";
s.subjectField.sqft = "SqFt";

//Immediately search for sales when a menu item has changed.
s.autoSearch = false;

// Access levels.
s.accessLevel = 0; //Overall access level.
s.accessLevel_neighborhood = 0;
s.accessLevel_salesAverage = 0;
s.accessLevel_report = 0;

// Sales search radiuses.
s.searchRadius_list = ["Any", "0.25", "0.5", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "20"];

s.salesResultsTools = ["Zoom all", "Clear"];
s.salesResultsMoreTools = ["Comp Report", "Heat Map", "CSV"]; // "Table View", Add a leading blank space to comment out tools.

//Get property type spec.
export function getPropTypeSpec(name: string): iPropType {
   for (const propType of s.propTypes) {
      if (propType.name === name) {
         return propType;
      }
   }
   return null;
}
//Get improvement type spec.
export function getImproveTypeSpec(name: string): iImproveType {
   for (const improveType of s.improveTypes) {
      if (improveType.name === name) {
         return improveType;
      }
   }
   return null;
}

//Get omit improvement type spec.
export function getOmitImproveTypeSpec(improveType: string): iOmitImproveTypeCriteria {
   for (const omitCriteria of s.omitImproveTypeCriterias) {
      if (omitCriteria.improveType === improveType) {
         return omitCriteria;
      }
   }
   return null;
}
